import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import {
  FitnessCenter as FitnessCenterIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  CalendarMonth as CalendarMonthIcon,
  EmojiEvents as EmojiEventsIcon,
  ShowChart as ShowChartIcon,
} from "@mui/icons-material";
import NewColors from "../../../shared/theme/NewColors";
import { formatNumber, formatWeight } from "../../../lib/formatNumber";

const EXERCISE_ROWS_INITIAL = 6;
const PR_ROWS_MAX = 8;
const TAB_RECORDS = 0;
const TAB_TRENDS = 1;

function SummaryCard({ title, value, subtitle, icon: Icon, color }) {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: NewColors.cardBackground,
        height: "100%",
        transition: "box-shadow 0.2s ease",
        "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.06)" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        {Icon && (
          <Box
            sx={{
              color: color ?? "primary.main",
              mt: 0.25,
            }}
          >
            <Icon sx={{ fontSize: 28 }} />
          </Box>
        )}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={500}>
            {title}
          </Typography>
          <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mt: 0.25 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function hasPositiveProgress(ex) {
  const weightUp = ex.lastMaxWeight > ex.firstMaxWeight;
  const volUp = ex.lastVolume > ex.firstVolume;
  const showingWeight = ex.lastMaxWeight > 0 || ex.firstMaxWeight > 0;
  return showingWeight ? weightUp : volUp;
}

function exerciseTrendMeta(ex, weightUnit) {
  const weightUp = ex.lastMaxWeight > ex.firstMaxWeight;
  const volUp = ex.lastVolume > ex.firstVolume;
  const showingWeight = ex.lastMaxWeight > 0 || ex.firstMaxWeight > 0;
  const isPositive = showingWeight ? weightUp : volUp;
  const isNegative = showingWeight ? ex.lastMaxWeight < ex.firstMaxWeight : ex.lastVolume < ex.firstVolume;
  const TrendIcon = isPositive ? TrendingUpIcon : isNegative ? TrendingDownIcon : TrendingFlatIcon;
  const trendColor = isPositive
    ? NewColors.status.success
    : isNegative
      ? NewColors.status.error
      : NewColors.text.tertiary;
  const rangeLabel = showingWeight
    ? `${formatWeight(ex.firstMaxWeight)} → ${formatWeight(ex.lastMaxWeight)} ${weightUnit}`
    : `${formatNumber(ex.firstVolume, 0)} → ${formatNumber(ex.lastVolume, 0)} vol`;

  let pctLabel = null;
  if (showingWeight && ex.firstMaxWeight > 0) {
    const pct = ((ex.lastMaxWeight - ex.firstMaxWeight) / ex.firstMaxWeight) * 100;
    pctLabel = `${pct >= 0 ? "+" : ""}${formatNumber(pct, 0)}%`;
  } else if (!showingWeight && ex.firstVolume > 0) {
    const pct = ((ex.lastVolume - ex.firstVolume) / ex.firstVolume) * 100;
    pctLabel = `${pct >= 0 ? "+" : ""}${formatNumber(pct, 0)}%`;
  }

  return { TrendIcon, trendColor, rangeLabel, isPositive, isNegative, pctLabel };
}

const tableCellExercise = {
  wordBreak: "break-word",
  overflowWrap: "anywhere",
  verticalAlign: "top",
};

const ProgressInsightsSection = ({ metrics, loading, weightUnit = "lbs" }) => {
  const theme = useTheme();
  const [exerciseRowsOpen, setExerciseRowsOpen] = useState(false);
  const [detailTab, setDetailTab] = useState(TAB_RECORDS);

  const sortedExercises = useMemo(() => {
    const list = metrics?.exercisesWithProgression;
    if (!list?.length) return [];
    return [...list].sort(
      (a, b) => (hasPositiveProgress(b) ? 1 : 0) - (hasPositiveProgress(a) ? 1 : 0)
    );
  }, [metrics?.exercisesWithProgression]);

  if (loading) {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
          Progress insights
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            textAlign: "center",
          }}
        >
          <Typography color="text.secondary">Loading your progress…</Typography>
        </Paper>
      </Box>
    );
  }

  const hasWorkouts = metrics && metrics.workoutsCompleted > 0;

  if (!hasWorkouts) {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="h6" color="primary" sx={{ mb: 2, fontWeight: 700 }}>
          Progress insights
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 2,
            border: `1px dashed ${theme.palette.divider}`,
            backgroundColor: NewColors.sectionBackground,
            textAlign: "center",
          }}
        >
          <FitnessCenterIcon sx={{ fontSize: 48, color: NewColors.text.tertiary, mb: 1 }} />
          <Typography color="text.secondary">
            Complete workouts to see your progress insights.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Your last 90 days of exercise data will appear here.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const {
    workoutsCompleted,
    workoutsPerWeek,
    totalVolume,
    avgWeeklyVolumeLoad,
    weeklyVolumeSeries,
    consecutiveWeeks,
    mostImproved,
    personalRecords,
  } = metrics;
  const avgWorkoutsPerWeek = workoutsPerWeek?.avg ?? 0;
  const activeWorkoutWeeks = workoutsPerWeek?.perWeek?.length ?? 0;
  const volumeWeekCount = weeklyVolumeSeries?.length ?? 0;
  const prCount = personalRecords?.length ?? 0;
  const prList = (personalRecords ?? []).slice(0, PR_ROWS_MAX);

  const exerciseVisibleCount = exerciseRowsOpen ? sortedExercises.length : EXERCISE_ROWS_INITIAL;
  const exerciseRows = sortedExercises.slice(0, exerciseVisibleCount);
  const exerciseHidden = sortedExercises.length > EXERCISE_ROWS_INITIAL;

  const hasDetails = mostImproved || sortedExercises.length > 0 || prCount > 0;
  const hasPrTable = prCount > 0 && prList.length > 0;
  const hasExerciseTable = sortedExercises.length > 0;
  const showDetailTabs = hasPrTable && hasExerciseTable;

  return (
    <Box>
      <Typography variant="h6" color="primary" sx={{ mb: 0.5, fontWeight: 700 }}>
        Progress insights
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Last 90 days · {workoutsCompleted} workout{workoutsCompleted !== 1 ? "s" : ""} completed
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Workouts completed"
            value={workoutsCompleted}
            subtitle={null}
            icon={CalendarMonthIcon}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Workouts per week"
            value={activeWorkoutWeeks > 0 ? formatNumber(avgWorkoutsPerWeek, 1) : "—"}
            subtitle={
              activeWorkoutWeeks > 0
                ? `Avg across ${activeWorkoutWeeks} active week${activeWorkoutWeeks !== 1 ? "s" : ""}`
                : null
            }
            icon={ShowChartIcon}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Consistency"
            value={
              consecutiveWeeks > 0
                ? `${consecutiveWeeks} week${consecutiveWeeks !== 1 ? "s" : ""} in a row`
                : "—"
            }
            subtitle={consecutiveWeeks > 0 ? "Current streak" : "Start a streak this week"}
            icon={TrendingUpIcon}
            color={NewColors.status.success}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Total volume"
            value={totalVolume > 0 ? `${formatNumber(totalVolume, 0)} ${weightUnit}` : "—"}
            subtitle="Weight × reps (all sets)"
            icon={FitnessCenterIcon}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Volume per week"
            value={
              volumeWeekCount > 0 && (avgWeeklyVolumeLoad ?? 0) > 0
                ? `${formatNumber(avgWeeklyVolumeLoad, 0)} ${weightUnit}`
                : "—"
            }
            subtitle={
              volumeWeekCount > 0
                ? `Avg across ${volumeWeekCount} training week${volumeWeekCount !== 1 ? "s" : ""}`
                : null
            }
            icon={FitnessCenterIcon}
            color="secondary.main"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={4}>
          <SummaryCard
            title="Personal records"
            value={prCount > 0 ? prCount : "—"}
            subtitle={prCount > 0 ? "Heaviest set this period" : null}
            icon={EmojiEventsIcon}
            color="secondary.main"
          />
        </Grid>
      </Grid>

      {hasDetails && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            p: { xs: 1.5, sm: 2 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: "background.paper",
          }}
        >
          {mostImproved && (
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 1.5,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: `${NewColors.status.success}12`,
              }}
            >
              <Typography variant="overline" color="text.secondary" fontWeight={700} sx={{ letterSpacing: 0.6 }}>
                Standout this period
              </Typography>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary" sx={{ mt: 0.25 }}>
                {mostImproved.exerciseName || "This exercise"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {mostImproved.metric === "weight"
                  ? `${formatWeight(mostImproved.first)} → ${formatWeight(mostImproved.last)} ${weightUnit} (+${formatNumber(mostImproved.changePct, 0)}%)`
                  : `Total load +${formatNumber(mostImproved.changePct, 0)}% this period`}
              </Typography>
            </Box>
          )}

          {showDetailTabs && (
            <Tabs
              value={detailTab}
              onChange={(_, v) => setDetailTab(v)}
              variant="fullWidth"
              sx={{
                mb: 2,
                borderBottom: 1,
                borderColor: "divider",
                minHeight: 44,
                "& .MuiTab-root": { minHeight: 44, textTransform: "none", fontWeight: 600 },
              }}
            >
              <Tab
                value={TAB_RECORDS}
                label={`Personal records (${prList.length})`}
                id="progress-insights-tab-records"
                aria-controls="progress-insights-panel-records"
              />
              <Tab
                value={TAB_TRENDS}
                label={`Exercise trends (${sortedExercises.length})`}
                id="progress-insights-tab-trends"
                aria-controls="progress-insights-panel-trends"
              />
            </Tabs>
          )}

          {(!showDetailTabs || detailTab === TAB_RECORDS) && hasPrTable && (
            <Box
              id="progress-insights-panel-records"
              role="tabpanel"
              hidden={showDetailTabs && detailTab !== TAB_RECORDS}
              aria-labelledby="progress-insights-tab-records"
              sx={{ display: showDetailTabs && detailTab !== TAB_RECORDS ? "none" : "block" }}
            >
              {!showDetailTabs && (
                <>
                  <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 0.25 }}>
                    Personal records
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Best completed set per exercise (last 90 days)
                  </Typography>
                </>
              )}
              {showDetailTabs && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Best completed set per exercise (last 90 days)
                </Typography>
              )}
              <TableContainer
                sx={{
                  maxHeight: 360,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "auto",
                  overflowX: "hidden",
                  width: "100%",
                }}
              >
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "44%" }} />
                    <col style={{ width: "36%" }} />
                    <col style={{ width: "20%" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Exercise</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        Best set
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        Date
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {prList.map((pr) => (
                      <TableRow key={pr.exerciseId} hover sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}>
                        <TableCell sx={tableCellExercise}>
                          <Tooltip title={pr.exerciseName || ""} placement="top-start">
                            <Typography variant="body2" fontWeight={600}>
                              {pr.exerciseName}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: "top" }}>
                          <Typography variant="body2" color="text.secondary">
                            {pr.weight > 0
                              ? `${formatWeight(pr.weight)} ${weightUnit} × ${formatNumber(pr.reps)}`
                              : `${formatNumber(pr.reps)} reps`}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                          <Typography variant="body2" color="text.secondary">
                            {pr.date
                              ? (pr.date instanceof Date ? pr.date : new Date(pr.date)).toLocaleDateString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                })
                              : "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {(!showDetailTabs || detailTab === TAB_TRENDS) && hasExerciseTable && (
            <Box
              id="progress-insights-panel-trends"
              role="tabpanel"
              hidden={showDetailTabs && detailTab !== TAB_TRENDS}
              aria-labelledby="progress-insights-tab-trends"
              sx={{
                display: showDetailTabs && detailTab !== TAB_TRENDS ? "none" : "block",
              }}
            >
              {!showDetailTabs && (
                <>
                  <Typography variant="subtitle1" fontWeight={700} color="primary" sx={{ mb: 0.25 }}>
                    Exercise trends
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    Start → end of period (sorted with gains first)
                  </Typography>
                </>
              )}
              {showDetailTabs && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
                  Start → end of period (sorted with gains first)
                </Typography>
              )}
              <TableContainer
                sx={{
                  maxHeight: 380,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  overflow: "auto",
                  overflowX: "hidden",
                  width: "100%",
                }}
              >
                <Table size="small" stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
                  <colgroup>
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "40%" }} />
                    <col style={{ width: "16%" }} />
                  </colgroup>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Exercise</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        Sessions
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper" }}>Progress</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: "background.paper" }}>
                        Trend
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {exerciseRows.map((ex) => {
                      const { TrendIcon, trendColor, rangeLabel, isPositive, isNegative, pctLabel } = exerciseTrendMeta(
                        ex,
                        weightUnit
                      );
                      return (
                        <TableRow key={ex.exerciseId} hover sx={{ "&:nth-of-type(odd)": { bgcolor: "action.hover" } }}>
                          <TableCell sx={tableCellExercise}>
                            <Tooltip title={ex.exerciseName || ""}>
                              <Typography variant="body2" fontWeight={600}>
                                {ex.exerciseName}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: "top" }}>
                            <Typography variant="body2">{ex.sessions}</Typography>
                          </TableCell>
                          <TableCell sx={{ verticalAlign: "top", ...tableCellExercise }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8125rem" }}>
                              {rangeLabel}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ verticalAlign: "top" }}>
                            <Box
                              sx={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                                px: 1,
                                py: 0.25,
                                borderRadius: 10,
                                maxWidth: "100%",
                                flexWrap: "wrap",
                                bgcolor: isPositive
                                  ? `${NewColors.status.success}16`
                                  : isNegative
                                    ? `${NewColors.status.error}16`
                                    : "action.hover",
                              }}
                            >
                              <TrendIcon sx={{ fontSize: 18, color: trendColor, flexShrink: 0 }} />
                              <Typography variant="caption" fontWeight={700} color="text.primary">
                                {pctLabel ?? (isPositive ? "Up" : isNegative ? "Down" : "Flat")}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {exerciseHidden && (
                <Button size="small" onClick={() => setExerciseRowsOpen((o) => !o)} sx={{ mt: 1, textTransform: "none" }}>
                  {exerciseRowsOpen
                    ? "Show fewer"
                    : `Show ${sortedExercises.length - EXERCISE_ROWS_INITIAL} more`}
                </Button>
              )}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ProgressInsightsSection;
